import React from 'react';

import { tracking } from '@iwoca/frontend-tracking-library';
import { Button, Icon } from '@iwoca/orion';
import classNames from 'classnames';
import _ from 'lodash';
import { FormattedMessage } from 'react-intl';
import { reduxForm, getValues } from 'redux-form';
import { v4, v1 } from 'uuid';

import messages from './AdditionalGuarantor.messages';
import styles from './AdditionalGuarantor.module.css';
import fields from './fields';
import schemaValidator from './validate';
import {
    toggleManualAddress,
    saveCustomerState,
    removePersonManualAddressState
} from '../../actions';
import InternalLink from '../../components/InternalLink';
import { buildApplicationState } from '../../helpers/applicationManager';
import applicationSchema from '../../helpers/applicationSchema';
import { userIsIndicativeOfferFlow } from '../../helpers/user';
import { getAmplitudeDefaultEventProperties } from '../../tracking/amplitudeEvents';
import Person from '../PersonalInformation/Person';
import { guarantorPersonalInformationSchema } from '../PersonalInformation/schema';

const asyncValidate = schemaValidator(guarantorPersonalInformationSchema);

class AdditionalGuarantor extends React.Component {
    // eslint-disable-next-line react/no-deprecated
    componentWillMount() {
        this.uidToBeRemoved = null;
        this.props.handleHideCallback(this.onClose);
    }

    componentDidMount() {
        const {
            guarantors,
            uid,
            fields: { people },
            ui
        } = this.props;

        tracking.ampli.additionalGuarantorModalOpened({
            'Active step': _.capitalize(_.startCase(ui?.activeStep?.id)),
            ...getAmplitudeDefaultEventProperties()
        });

        const existingGuarantor = _.find(guarantors, { uid });
        if (!existingGuarantor) {
            this.newPersonUID = v1();
            let emptyPerson = _.merge(
                {},
                _.first(applicationSchema.people),
                {
                    uid: this.newPersonUID,
                    roles: ['guarantor'],
                    emailUID: v4()
                },
                { phone: [{ uid: v4() }, { uid: v4() }] },
                { addresses: [{ uid: v1() }] }
            );
            this.props.fields.people.addField(emptyPerson);
            return;
        }
        if (!_.find(people, { uid: { value: uid } }) && existingGuarantor) {
            people.addField(existingGuarantor);
            this.cachedPersonValues = existingGuarantor;
            return;
        }
        this.cachedPersonValues = _.clone(
            _.find(this.props.values.people, { uid })
        );
    }

    // eslint-disable-next-line react/no-deprecated
    componentWillReceiveProps(nextProps) {
        const {
            uid,
            guarantors,
            fields: { people }
        } = this.props;
        const existingGuarantor = _.find(guarantors, { uid });

        if (_.find(people, { uid: { value: this.uidToBeRemoved } })) {
            let existingGuarantorIndex = _.findIndex(people, {
                uid: { value: this.uidToBeRemoved }
            });
            people.removeField(existingGuarantorIndex);
            this.uidToBeRemoved = null;
        }

        if (!this.cachedPersonValues && existingGuarantor) {
            // on first mount this.props.values.people are empty, requires caching on next props
            this.cachedPersonValues = _.clone(
                _.find(nextProps.values.people, { uid })
            );
        }
    }

    componentWillUnmount() {
        this.props.closeModal();
    }

    /**
     * Dispatches a ADDRESS_FETCH_REQUESTED action
     * @param {String} input
     */
    onAddressChange = (input) => {
        this.props.fetchAddressLookup(input);
    };

    onAddressSelect = (addressIndex, address) => {
        const guarantor = this.getGuarantorField();
        const { people: peopleValues } = this.props.values;
        const personValues = _.find(peopleValues, { uid: guarantor.uid.value });
        let newAddress = _.assign(
            {},
            personValues.addresses[addressIndex],
            address
        );
        if (_.isEmpty(address)) {
            let emptyAddress = {
                house_name: '',
                house_number: '',
                street_line_1: '',
                town: '',
                postcode: '',
                equifax_token: ''
            };
            _.assign(newAddress, emptyAddress);
        }
        guarantor.addresses.removeField(addressIndex);
        guarantor.addresses.addField(newAddress, addressIndex);
    };

    getGuarantorField = () => {
        const {
            fields: { people },
            uid
        } = this.props;
        const existingGuarantor = _.find(people, { uid: { value: uid } });
        const newPerson = _.find(people, { uid: { value: this.newPersonUID } });
        return existingGuarantor ? existingGuarantor : newPerson;
    };

    /**
     * Close the modal and reset the selected values to the cached ones
     */
    onClose = () => {
        const {
            fields: { people },
            uid
        } = this.props;
        const newPersonIndex = _.findIndex(people, {
            uid: { value: this.newPersonUID }
        });
        const existingGuarantorIndex = _.findIndex(people, {
            uid: { value: uid }
        });
        if (newPersonIndex > -1) {
            people.removeField(newPersonIndex);
        }
        if (existingGuarantorIndex > -1) {
            people.removeField(existingGuarantorIndex);
            people.addField(this.cachedPersonValues);
        }
        this.props.closeModal();
    };

    /**
     * Update the form dob month state with a selected value from dob months list
     * @param {Number} personIndex
     * @param {Number} month => June = 6, July = 7 etc.
     */
    onMonthSelect = (month) => {
        let val = (month && month.value) || '';
        const guarantor = this.getGuarantorField();
        guarantor.date_of_birth.mm.onChange(val);
    };

    /**
     * Submits the form and closes modal on success
     */
    saveEdit = () => {
        const guarantor = this.getGuarantorField();
        if (!guarantor.uid.value) {
            guarantor.uid.onChange(v1());
        }
        const { ui, formValues, application, closeModal, saveCustomerState } =
            this.props;

        tracking.ampli.additionalGuarantorAdded({
            'Active step': _.capitalize(_.startCase(ui?.activeStep?.id)),
            ...getAmplitudeDefaultEventProperties()
        });

        const newState = buildApplicationState(application.payload, formValues);
        const stateToSave = {
            data: {
                application: newState,
                ui
            }
        };
        saveCustomerState(stateToSave);
        closeModal();
    };

    /**
     * Removes existing guarantor, submit the state, and closes modal.
     */
    removeGuarantor = async () => {
        const {
            fields: { people },
            uid,
            saveCustomerState,
            closeModal,
            removeGuarantor,
            removePersonManualAddressState
        } = this.props;

        await Promise.resolve()
            .then(() => {
                const existingGuarantorIndex = _.findIndex(people, {
                    uid: { value: uid }
                });
                people.removeField(existingGuarantorIndex);
                this.uidToBeRemoved = uid;
            })
            .then(() => {
                removeGuarantor(uid);
                removePersonManualAddressState(uid);
            })
            .then(() => {
                const { ui, formValues, application } = this.props;
                const state = buildApplicationState(
                    application.payload,
                    formValues
                );
                const stateToSave = {
                    data: {
                        application: state,
                        ui
                    }
                };
                saveCustomerState(stateToSave);
            });
        closeModal();
    };

    render() {
        const {
            ui: { manualAddresses },
            fetched: { addresses: fetchedAddresses },
            handleSubmit,
            fields: { people },
            toggleManualAddress,
            isInIndicativeOfferFlow
        } = this.props;
        const guarantor = this.getGuarantorField();
        const modalTitle =
            guarantor && guarantor.uid.value === this.newPersonUID ? (
                <FormattedMessage {...messages.addGuarantor} />
            ) : (
                <FormattedMessage {...messages.editGuarantorDetails} />
            );
        return (
            <form
                data-testid="additional-guarantor-form"
                autoComplete="off"
                className={classNames(
                    styles.additionalGuarantorForm,
                    'form-horizontal'
                )}
                onSubmit={handleSubmit(this.saveEdit)}>
                <div className={styles.guarantorHeaderContainer}>
                    <div className={styles.guarantorHeader}>
                        {modalTitle}{' '}
                        {guarantor &&
                        guarantor.uid.value !== this.newPersonUID ? (
                            <div className="toggle-role">
                                <InternalLink onClick={this.removeGuarantor}>
                                    <FormattedMessage
                                        {...messages.removeGuarantor}
                                    />
                                </InternalLink>
                            </div>
                        ) : null}
                    </div>
                    <Button
                        data-testid="additional-guarantor-close"
                        onClick={this.onClose}
                        className={styles.closeIcon}
                        aria-label="Close"
                        variant="tertiary"
                        iconOnly>
                        <Icon icon="timesDefault" />
                    </Button>
                </div>
                <div>
                    {guarantor ? (
                        <Person
                            toggleManualAddress={toggleManualAddress}
                            onAddressChange={this.onAddressChange}
                            onAddressSelect={this.onAddressSelect}
                            onMonthSelect={this.onMonthSelect}
                            person={guarantor}
                            personIndex={
                                _.findIndex(
                                    people,
                                    (person) =>
                                        person.uid.value === guarantor.uid.value
                                ) + 1
                            }
                            fetchedAddresses={fetchedAddresses}
                            manualAddressesState={manualAddresses}
                            formLayout="guarantor"
                            isInIndicativeOfferFlow={isInIndicativeOfferFlow}
                        />
                    ) : null}
                    <div className={styles.submitContainer}>
                        <Button type="submit">
                            <FormattedMessage
                                {...messages.saveAndReturnToApplication}
                            />
                        </Button>
                    </div>
                </div>
            </form>
        );
    }
}

export default reduxForm(
    {
        form: 'sign_up',
        formKey: 'additionalGuarantor',
        destroyOnUnmount: false,
        getFormState: (state, reduxMountPoint) => state[reduxMountPoint],
        fields,
        asyncValidate,
        alwaysAsyncValidate: true,
        asyncBlurFields: _.without(
            fields,
            'people[].privacy_policy_agreement_date'
        )
    },
    (state) => ({
        fetched: state.fetched,
        application: state.fetched.application,
        formValues: getValues(state.form.sign_up),
        ui: state.ui,
        isInIndicativeOfferFlow: userIsIndicativeOfferFlow(state.ui),
        initialValues: _.merge(
            {},
            {
                people: _.chain(state.fetched.application.payload.people)
                    .filter(
                        (person) =>
                            _.includes(person.roles, 'guarantor') &&
                            !_.includes(person.roles, 'applicant')
                    )
                    .map((person) =>
                        _.merge({}, _.first(applicationSchema.people), person)
                    )
                    .value()
            },
            {
                people: getValues(state.form.sign_up).additionalGuarantor
                    ? getValues(state.form.sign_up).additionalGuarantor.people
                    : null
            }
        ),
        ...state.additionalGuarantor
    }),
    (dispatch) => ({
        toggleManualAddress: (personUID, addressIndex) =>
            dispatch(toggleManualAddress(personUID, addressIndex)),
        removePersonManualAddressState: (personUID) =>
            dispatch(removePersonManualAddressState(personUID)),
        fetchAddressLookup: (postcode) =>
            dispatch({ type: 'ADDRESSES_FETCH_REQUESTED', postcode }),
        saveCustomerState: (state) => dispatch(saveCustomerState(state)),
        removeGuarantor: (uid) => dispatch({ type: 'REMOVE_GUARANTOR', uid })
    }),
    null,
    { withRef: true }
)(AdditionalGuarantor);
